import { useStore } from '@stores';
import { isDemoTenant } from '@lib';
import { useCallback, useEffect } from 'react';

/**
 * The vehicle lookup allowance on a demo account.
 *
 * Enforced here in the portal: `exhausted` disables the search, and `consume` spends a
 * credit once a lookup has succeeded. The server owns the arithmetic — it decides whether
 * the tenant is metered, what a lookup costs, and whether the balance covers it — so the
 * remaining count always comes back from the server rather than being tracked locally.
 *
 * remaining is null for paying tenants, who are not metered at all, and also while the
 * first balance is still in flight.
 */
export const useDemoLookupAllowance = () => {
  const { creditStore } = useStore();
  const isDemo = isDemoTenant();

  // The store is shared, so several cards on one page do not each refetch. `consume`
  // writes the server's new balance back, which keeps this from going stale.
  const loaded = creditStore.balance !== null;

  useEffect(() => {
    if (isDemo && !loaded) {
      creditStore.getBalance();
    }
  }, [isDemo, loaded, creditStore]);

  const consume = useCallback(async () => {
    if (isDemo) {
      await creditStore.consumeVehicleLookup();
    }
  }, [isDemo, creditStore]);

  const remaining = isDemo && loaded ? (creditStore.balance?.balance ?? 0) : null;

  return {
    isDemo,
    remaining,
    // Never claim the allowance is spent before the balance has arrived, or a demo tenant
    // sees a disabled search and "0 left" on every mount until the request lands.
    exhausted: isDemo && loaded && (remaining ?? 0) <= 0,
    consume,
  };
};
